import * as React from 'react';
import { Edit2, CheckCircle, XCircle, Clock, Trash2, Link2OffIcon, Link2Off } from 'lucide-react';

import {
  useDeleteEnquiryMutation,
  useChangeEnquiryStatusMutation,
} from '@/store/enquiryApi';
import { useSelector } from 'react-redux';
import { RootState } from '@/types';
import { toast } from 'sonner';
import { Dialog, DialogContent } from './ui/dialog';
import AddEnquiry from './AddEnquiry';

const EnquiryActionsDropdown = ({ enquiry }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const { user } = useSelector((state: RootState) => state.user);
  const [DeleteEnqFn, { isSuccess }] = useDeleteEnquiryMutation();
  const [editEnqFn, { isSuccess: editSuccess }] =
    useChangeEnquiryStatusMutation();

  React.useEffect(() => {
    if (isSuccess) {
      toast.success('Enquiry has been Deleted');
    }
  }, [isSuccess, editSuccess]);

  const handleAction = (action: string) => {
    // Handle the action here
    editEnqFn({
      id: enquiry._id,
      Token: user?.token,
      data: { status: action },
    });

    setIsOpen(false);
  };

  const handleDelete = () => {
    DeleteEnqFn({
      id: enquiry._id,
      Token: user?.token,
    });
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="transition-opacity p-2 hover:bg-gray-100 rounded-full"
      >
        <Edit2 className="h-4 w-4 text-gray-500" />
      </button>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-7xl min-h-screen bottom-0 overflow-y-auto">
          <AddEnquiry enquiry={enquiry} />
        </DialogContent>
      </Dialog>

      {isOpen && (
        <div
          className="absolute right-0 z-10 mt-1 w-48 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 divide-y divide-gray-100 transform origin-top-right transition-all duration-200 ease-out animate-in fade-in slide-in-from-top-2"
          onMouseLeave={() => setIsOpen(false)}
        >
          <div className="py-1">
            {/* Open Status */}
            <button
              onClick={() => handleAction('Open')}
              className="group flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-teal-50 transition-colors duration-150"
            >
              <CheckCircle className="mr-3 h-4 w-4 text-gray-400 group-hover:text-[#26BDB0] transition-colors duration-150" />
              <span className="group-hover:text-[#49ccc1e2]">Open</span>
            </button>

            {/* Close Status */}
            <button
              onClick={() => handleAction('Closed')}
              className="group flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-red-50 transition-colors duration-150"
            >
              <XCircle className="mr-3 h-4 w-4 text-gray-400 group-hover:text-red-500 transition-colors duration-150" />
              <span className="group-hover:text-red-700">Close</span>
            </button>

            {/* Registered Status */}
            <button
              onClick={() => handleAction('Registered')}
              className="group flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-teal-100 transition-colors duration-150"
            >
              <Link2Off className="mr-3 h-4 w-4 text-gray-400 group-hover:text-[#127971] transition-colors duration-150" />
              <span className="group-hover:text-[#26BDB0]">Registered</span>
            </button>

            {/* Pending Status */}
            <button
              onClick={() => handleAction('Pending')}
              className="group flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-yellow-50 transition-colors duration-150"
            >
              <Clock className="mr-3 h-4 w-4 text-gray-400 group-hover:text-yellow-500 transition-colors duration-150" />
              <span className="group-hover:text-yellow-700">Pending</span>
            </button>
          </div>

          {/* Actions Section */}
          <div className="py-1">
            <button
              onClick={() => setIsDialogOpen(true)}
              className="group flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 transition-colors duration-150"
            >
              <Edit2 className="mr-3 h-4 w-4 text-gray-400 group-hover:text-blue-500 transition-colors duration-150" />
              <span className="group-hover:text-blue-700">Edit</span>
            </button>

            <button
              onClick={handleDelete}
              className="group flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-red-50 transition-colors duration-150"
            >
              <Trash2 className="mr-3 h-4 w-4 text-gray-400 group-hover:text-red-500 transition-colors duration-150" />
              <span className="group-hover:text-red-700">Delete</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnquiryActionsDropdown;
